import { create } from 'zustand';
import { agentApi } from '@/shared/api/agentApi';

export type WrapupMode = 'modal' | 'sidebar' | 'off';

interface WrapupSettingsStore {
  /** How the call-summary is shown to this agent, set by the business admin. */
  mode: WrapupMode;
  loaded: boolean;
  /** Fetch the tenant's wrap-up mode from /agent/settings. Safe to call once. */
  load: () => Promise<void>;
  setMode: (m: WrapupMode) => void;
}

/**
 * Tenant-level wrap-up display preference for the agent UI. The business owner
 * picks modal (blocking, default), sidebar (non-blocking), or off in admin
 * settings; it arrives on /agent/settings as `wrapup_mode`. Loaded once at
 * agent layout mount so the choice is known before any call ends. Defaults to
 * 'modal' until loaded, preserving the original blocking behavior.
 */
export const useWrapupSettings = create<WrapupSettingsStore>((set, get) => ({
  mode: 'modal',
  loaded: false,
  async load() {
    if (get().loaded) return;
    try {
      const d = (await agentApi.settings()) as { wrapup_mode?: WrapupMode } | undefined;
      const m = d?.wrapup_mode;
      set({ mode: m === 'sidebar' || m === 'off' ? m : 'modal', loaded: true });
    } catch {
      // Network/perms failure → keep the safe default (blocking modal).
      set({ loaded: true });
    }
  },
  setMode: (m) => set({ mode: m }),
}));
